import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  ChevronLeft, File, FileText, Image, Loader2, MessageSquare,
  Music, Paperclip, Search, Send, Video, X,
} from 'lucide-react'
import { messagesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'
import {
  connectMessageSocket,
  getMessageSocketConnectionStatus,
  subscribeToMessageSocketStatus,
} from '../services/messageSocket.js'

// ── Helpers ───────────────────────────────────────────────────────────────────
const getEntityId = (v) => (!v ? '' : typeof v === 'string' ? v : v._id || v.id || '')

const getUserName = (u) =>
  u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Unknown'

const fmtTime = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

const fmtThreadDate = (v) => {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return fmtTime(v)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

const fmtFileSize = (bytes) => {
  if (!bytes) return ''
  return bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`
}

const getFileType = (file) => {
  const mime = file?.type || ''
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  return 'file'
}

const getMessageTimestamp = (item) => {
  const parsed = new Date(item?.createdAt || item?.updatedAt || 0).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

const sortThreadsByLatest = (items = []) =>
  [...items].sort((a, b) => getMessageTimestamp(b) - getMessageTimestamp(a))

const appendMessageIfMissing = (items = [], message) => {
  const messageId = getEntityId(message)
  if (!messageId) return items
  if (items.some((item) => getEntityId(item) === messageId)) return items
  return [...items, message].sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b))
}

const normalizeUserFromMessage = (user) => {
  if (!user || typeof user !== 'object') return user
  const id = getEntityId(user)
  return {
    ...user,
    _id: id,
    id,
    name: getUserName(user),
  }
}

const getOtherUserFromMessage = (message, currentUserId) => {
  const sender = message?.senderId
  const receiver = message?.receiverId
  const senderId = getEntityId(sender)
  return normalizeUserFromMessage(senderId === currentUserId ? receiver : sender)
}

const upsertThreadPreview = ({ threads = [], message, currentUserId, activeUserId }) => {
  if (!message || !currentUserId) return threads

  const senderId = getEntityId(message.senderId)
  const receiverId = getEntityId(message.receiverId)
  const threadId = message.threadId || [senderId, receiverId].filter(Boolean).sort().join('_')
  const otherUserId = senderId === currentUserId ? receiverId : senderId
  if (!threadId || !otherUserId) return threads

  const existingIndex = threads.findIndex((item) => item.threadId === threadId)
  const existingThread = existingIndex >= 0 ? threads[existingIndex] : null
  const isIncoming = receiverId === currentUserId && senderId !== currentUserId
  const isActiveThread = activeUserId === otherUserId
  const unreadCount = isActiveThread
    ? 0
    : Math.max(0, Number(existingThread?.unreadCount || 0) + (isIncoming ? 1 : 0))

  const nextThread = {
    ...(existingThread || {}),
    ...message,
    threadId,
    otherUser: getOtherUserFromMessage(message, currentUserId) || existingThread?.otherUser,
    unreadCount,
  }

  const nextThreads = existingIndex >= 0
    ? threads.filter((_, index) => index !== existingIndex)
    : [...threads]

  return sortThreadsByLatest([nextThread, ...nextThreads])
}

const markThreadRead = ({ threads = [], otherUserId }) =>
  threads.map((thread) => (
    getEntityId(thread.otherUser) === otherUserId
      ? { ...thread, unreadCount: 0 }
      : thread
  ))

const markMessagesRead = ({ messages = [], messageIds = [] }) => {
  const messageIdSet = new Set(messageIds.map(String))
  if (!messageIdSet.size) return messages

  return messages.map((message) => (
    messageIdSet.has(getEntityId(message))
      ? { ...message, isRead: true }
      : message
  ))
}

// ── Role styles ───────────────────────────────────────────────────────────────
const ROLE_STYLE = {
  admin:       { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-100' },
  super_admin: { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-100'    },
  teacher:     { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-100'   },
  student:     { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-100'  },
  parent:      { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-100'  },
}
const ROLE_LABELS = {
  admin: 'Admin', super_admin: 'Super Admin',
  teacher: 'Teacher', student: 'Student', parent: 'Parent',
}
const roleStyle = (role) => ROLE_STYLE[role] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' }

// ── Attachment bubble (inside message) ───────────────────────────────────────
function AttachmentBubble({ attachment, isMine }) {
  if (!attachment?.url) return null
  const { type, url, name, size } = attachment
  const sizeStr = fmtFileSize(size)
  const linkBase = `flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors ${
    isMine ? 'bg-white/15 hover:bg-white/20' : 'bg-gray-50 hover:bg-gray-100 border border-gray-100'
  }`

  if (type === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img src={url} alt={name || 'Image'} className="rounded-xl max-h-52 w-auto object-cover" />
      </a>
    )
  }

  const icons = { video: Video, audio: Music, file: File }
  const ext = name?.split('.').pop()?.toLowerCase() || ''
  const Icon = type === 'video' ? Video : type === 'audio' ? Music : ext === 'pdf' ? FileText : File
  const subLabel = type === 'video' ? 'Tap to play' : type === 'audio' ? 'Tap to play' : sizeStr

  return (
    <a href={url} target="_blank" rel="noreferrer" download className={linkBase}>
      <Icon className={`w-5 h-5 flex-shrink-0 ${isMine ? 'text-white/80' : 'text-primary-600'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium truncate ${isMine ? 'text-white' : 'text-gray-800'}`}>
          {name || 'Attachment'}
        </p>
        {subLabel && (
          <p className={`text-xs mt-0.5 ${isMine ? 'text-white/60' : 'text-gray-400'}`}>{subLabel}</p>
        )}
      </div>
    </a>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MessagesPage() {
  const { user: authUser, token } = useSelector((state) => state.auth)
  const currentUserId = getEntityId(authUser)
  const currentSchoolId = getEntityId(authUser?.schoolId || authUser?.school)
  const messagesEndRef = useRef(null)
  const imageInputRef = useRef(null)
  const fileInputRef = useRef(null)

  const [threads, setThreads] = useState([])
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [messages, setMessages] = useState([])
  const [loadingThreads, setLoadingThreads] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [threadSearch, setThreadSearch] = useState('')
  const [showNewChat, setShowNewChat] = useState(false)
  const [newChatSearch, setNewChatSearch] = useState('')
  const [draft, setDraft] = useState('')
  const [pendingFile, setPendingFile] = useState(null) // { file, type, preview? }
  const [sending, setSending] = useState(false)
  const [showAttachPicker, setShowAttachPicker] = useState(false)
  const [mobileInChat, setMobileInChat] = useState(false)
  const [socketConnected, setSocketConnected] = useState(getMessageSocketConnectionStatus())

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchThreads = useCallback(async (opts = {}) => {
    try {
      if (!opts.silent) setLoadingThreads(true)
      const data = await messagesApi.getThreads()
      const list = sortThreadsByLatest(Array.isArray(data) ? data : [])
      setThreads(list)

      setSelectedUser((current) => current || list[0]?.otherUser || null)
    } catch {
      if (!opts.silent) toast.error('Failed to load conversations')
    } finally {
      setLoadingThreads(false)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const data = await messagesApi.getUsers()
      setUsers(Array.isArray(data) ? data : [])
    } catch {
      setUsers([])
    }
  }, [])

  const fetchMessages = useCallback(async (userId, opts = {}) => {
    if (!userId) { setMessages([]); return }
    try {
      if (!opts.silent) setLoadingMessages(true)
      const data = await messagesApi.getMessages(userId)
      setMessages(Array.isArray(data) ? data : [])
    } catch {
      if (!opts.silent) toast.error('Failed to load messages')
    } finally {
      setLoadingMessages(false)
    }
  }, [])

  useEffect(() => {
    fetchThreads()
    fetchUsers()
  }, [fetchThreads, fetchUsers])

  useEffect(() => {
    return subscribeToMessageSocketStatus(setSocketConnected)
  }, [])

  useEffect(() => {
    const uid = getEntityId(selectedUser)
    if (!uid) return
    fetchMessages(uid)
  }, [fetchMessages, selectedUser])

  useEffect(() => {
    if (!token || !currentUserId) {
      return undefined
    }

    const socket = connectMessageSocket(token)
    if (!socket) return undefined

    const selectedUserId = getEntityId(selectedUser)

    const refreshActiveConversation = () => {
      fetchThreads({ silent: true })
      if (selectedUserId) fetchMessages(selectedUserId, { silent: true })
    }

    const handleMessageNew = (payload) => {
      if (payload?.schoolId && currentSchoolId && payload.schoolId !== currentSchoolId) return

      const message = payload?.message
      if (!message) return

      const senderId = getEntityId(message.senderId || payload.senderId)
      const receiverId = getEntityId(message.receiverId || payload.receiverId)
      const otherUserId = senderId === currentUserId ? receiverId : senderId

      setThreads((current) => upsertThreadPreview({
        threads: current,
        message,
        currentUserId,
        activeUserId: selectedUserId,
      }))

      if (selectedUserId && otherUserId === selectedUserId) {
        setMessages((current) => appendMessageIfMissing(current, message))

        if (receiverId === currentUserId && senderId !== currentUserId) {
          fetchMessages(otherUserId, { silent: true })
        }
      }
    }

    const handleMessageRead = (payload) => {
      const readerId = String(payload?.userId || '')
      const otherUserId = readerId === currentUserId
        ? String(payload?.otherUserId || '')
        : readerId
      const messageIds = Array.isArray(payload?.messageIds)
        ? payload.messageIds.map(String)
        : []

      if (readerId === currentUserId && otherUserId) {
        setThreads((current) => markThreadRead({ threads: current, otherUserId }))
      }

      if (selectedUserId && selectedUserId === otherUserId && messageIds.length) {
        setMessages((current) => markMessagesRead({ messages: current, messageIds }))
      }
    }

    socket.on('connect', refreshActiveConversation)
    socket.on('message:new', handleMessageNew)
    socket.on('message:read', handleMessageRead)

    return () => {
      socket.off('connect', refreshActiveConversation)
      socket.off('message:new', handleMessageNew)
      socket.off('message:read', handleMessageRead)
    }
  }, [currentSchoolId, currentUserId, fetchMessages, fetchThreads, selectedUser, token])

  useEffect(() => {
    const refreshOnFocus = () => {
      fetchThreads({ silent: true })
      const uid = getEntityId(selectedUser)
      if (uid) fetchMessages(uid, { silent: true })
    }

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') refreshOnFocus()
    }

    window.addEventListener('focus', refreshOnFocus)
    document.addEventListener('visibilitychange', refreshOnVisible)

    return () => {
      window.removeEventListener('focus', refreshOnFocus)
      document.removeEventListener('visibilitychange', refreshOnVisible)
    }
  }, [fetchMessages, fetchThreads, selectedUser])

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Derived lists ──────────────────────────────────────────────────────────
  const filteredThreads = useMemo(() => {
    const q = threadSearch.trim().toLowerCase()
    return threads.filter((t) => {
      if (!q) return true
      const name = getUserName(t.otherUser).toLowerCase()
      const preview = (t.attachment?.name || t.message || '').toLowerCase()
      return name.includes(q) || preview.includes(q) || (t.otherUser?.role || '').toLowerCase().includes(q)
    })
  }, [threads, threadSearch])

  const filteredUsers = useMemo(() => {
    const openIds = new Set(threads.map((t) => getEntityId(t.otherUser)))
    const q = newChatSearch.trim().toLowerCase()
    return users.filter((u) => {
      const uid = getEntityId(u)
      if (!uid || uid === currentUserId || openIds.has(uid)) return false
      if (!q) return true
      return [
        getUserName(u), u?.role, u?.email, u?.admissionNo,
        u?.classId?.name,
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    }).sort((a, b) => getUserName(a).localeCompare(getUserName(b), 'en', { sensitivity: 'base' }))
  }, [users, threads, newChatSearch, currentUserId])

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openThread = (user) => {
    setSelectedUser(user)
    setShowNewChat(false)
    setNewChatSearch('')
    setMobileInChat(true)
  }

  const openNewChat = () => {
    setShowNewChat(true)
    setNewChatSearch('')
  }

  const closeNewChat = () => {
    setShowNewChat(false)
    setNewChatSearch('')
  }

  const handleFileSelect = (e, forcedType) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const type = forcedType || getFileType(file)
    const preview = type === 'image' ? URL.createObjectURL(file) : null
    setPendingFile({ file, type, preview, name: file.name, size: file.size })
    setShowAttachPicker(false)
  }

  const clearPendingFile = () => {
    if (pendingFile?.preview) URL.revokeObjectURL(pendingFile.preview)
    setPendingFile(null)
  }

  const handleSend = async () => {
    const receiverId = getEntityId(selectedUser)
    if (!receiverId) return
    if (!draft.trim() && !pendingFile) return

    try {
      setSending(true)
      let payload

      if (pendingFile) {
        payload = new FormData()
        payload.append('receiverId', receiverId)
        if (draft.trim()) payload.append('message', draft.trim())
        payload.append('file', pendingFile.file)
      } else {
        payload = { receiverId, message: draft.trim() }
      }

      const created = await messagesApi.send(payload)
      setMessages((prev) => appendMessageIfMissing(prev, created))
      setThreads((prev) => upsertThreadPreview({
        threads: prev,
        message: created,
        currentUserId,
        activeUserId: getEntityId(selectedUser),
      }))
      setDraft('')
      clearPendingFile()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-100px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

      {/* ── LEFT: Thread list ──────────────────────────────────────────── */}
      <div className={`w-full xl:w-[320px] border-r border-gray-100 flex flex-col flex-shrink-0 ${mobileInChat ? 'hidden xl:flex' : 'flex'}`}>

        {showNewChat ? (
          /* New Chat panel */
          <>
            <div className="flex items-center justify-between gap-3 px-4 py-4 border-b border-gray-100">
              <div>
                <p className="font-semibold text-gray-900 text-sm">New conversation</p>
                <p className="text-xs text-gray-400 mt-0.5">Select someone to message</p>
              </div>
              <button onClick={closeNewChat} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-3 py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  value={newChatSearch}
                  onChange={(e) => setNewChatSearch(e.target.value)}
                  placeholder="Search by name, role, email…"
                  className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400"
                  autoFocus
                />
                {newChatSearch && (
                  <button onClick={() => setNewChatSearch('')} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center text-gray-400">
                  <MessageSquare className="w-8 h-8 mb-2 text-gray-200" />
                  <p className="text-sm">No users found</p>
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const rs = roleStyle(u.role)
                  const cls = u.classId
                  const secondaryLine = u.email || (u.admissionNo ? `Adm# ${u.admissionNo}` : null)
                  const tertiaryLine = cls ? `${cls.name}${cls.section ? ` ${cls.section}` : ''}` : null

                  return (
                    <button
                      key={getEntityId(u)}
                      onClick={() => openThread(u)}
                      className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Avatar src={u.avatar} name={getUserName(u)} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 text-sm truncate">{getUserName(u)}</p>
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${rs.bg} ${rs.text} ${rs.border}`}>
                            {ROLE_LABELS[u.role] || u.role || 'User'}
                          </span>
                        </div>
                        {secondaryLine && <p className="text-xs text-gray-500 truncate mt-0.5">{secondaryLine}</p>}
                        {tertiaryLine && <p className="text-xs text-gray-400 truncate">{tertiaryLine}</p>}
                      </div>
                      <MessageSquare className="w-4 h-4 text-primary-400 flex-shrink-0" />
                    </button>
                  )
                })
              )}
            </div>
          </>
        ) : (
          /* Thread list panel */
          <>
            <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 flex-1 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  value={threadSearch}
                  onChange={(e) => setThreadSearch(e.target.value)}
                  placeholder="Search conversations…"
                  className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400"
                />
                {threadSearch && (
                  <button onClick={() => setThreadSearch('')} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button
                onClick={openNewChat}
                className="btn-primary p-2 rounded-xl flex-shrink-0"
                title="New conversation"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingThreads ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              ) : filteredThreads.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center text-gray-400">
                  <MessageSquare className="w-10 h-10 mb-3 text-gray-200" />
                  <p className="text-sm font-medium text-gray-600">No conversations yet</p>
                  <p className="text-xs mt-1">Tap the compose button to start one</p>
                </div>
              ) : (
                filteredThreads.map((thread) => {
                  const other = thread.otherUser
                  const isSelected = getEntityId(other) === getEntityId(selectedUser)
                  const isUnread = (thread.unreadCount || 0) > 0
                  const preview = thread.attachment
                    ? `📎 ${thread.attachment.name || thread.attachment.type || 'Attachment'}`
                    : thread.message || 'No messages yet'

                  return (
                    <button
                      key={thread.threadId || getEntityId(other)}
                      onClick={() => openThread(other)}
                      className={`w-full px-4 py-3.5 border-b border-gray-50 text-left transition-colors ${
                        isSelected
                          ? 'bg-primary-50'
                          : isUnread
                          ? 'bg-primary-50/40 hover:bg-primary-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative flex-shrink-0">
                          <Avatar src={other?.avatar} name={getUserName(other)} size="md" />
                          {isUnread && (
                            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary-600 border-2 border-white" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-1">
                            <p className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
                              {getUserName(other)}
                            </p>
                            <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">
                              {fmtThreadDate(thread.createdAt)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 capitalize mt-0.5">{other?.role || 'user'}</p>
                          <div className="flex items-center justify-between gap-2 mt-1">
                            <p className={`text-xs truncate flex-1 ${isUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                              {preview}
                            </p>
                            {isUnread && thread.unreadCount > 0 && (
                              <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-600 text-white text-[10px] font-bold flex items-center justify-center">
                                {thread.unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT: Chat view ───────────────────────────────────────────── */}
      <div className={`flex-1 flex flex-col min-w-0 ${!mobileInChat && !selectedUser ? 'hidden xl:flex' : 'flex'}`}>
        {selectedUser ? (
          <>
            {/* Chat header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
              <button
                onClick={() => { setMobileInChat(false); setSelectedUser(null) }}
                className="xl:hidden text-gray-400 hover:text-gray-600 p-1"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Avatar src={selectedUser.avatar} name={getUserName(selectedUser)} size="md" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate">{getUserName(selectedUser)}</p>
                <p className="text-xs text-gray-500 capitalize mt-0.5">
                  {selectedUser.role || 'user'} • {socketConnected ? 'Live' : 'Sync on activity'}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4 space-y-3">
              {loadingMessages ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                  <p className="text-2xl mb-1">👋</p>
                  <p className="text-sm text-gray-500 font-medium">Say hello!</p>
                  <p className="text-xs text-gray-400 mt-1">Be the first to send a message</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMine = getEntityId(msg.senderId) === currentUserId
                  const sender = typeof msg.senderId === 'object' ? msg.senderId : selectedUser

                  return (
                    <div
                      key={msg._id || msg.id || `${msg.createdAt}-${msg.message}`}
                      className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                    >
                      {/* Avatar for received messages */}
                      {!isMine && (
                        <Avatar src={sender?.avatar} name={getUserName(sender)} size="sm" />
                      )}

                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
                        isMine
                          ? 'bg-primary-600 text-white rounded-br-sm'
                          : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100'
                      }`}>
                        {msg.attachment && (
                          <div className={msg.message ? 'mb-2' : ''}>
                            <AttachmentBubble attachment={msg.attachment} isMine={isMine} />
                          </div>
                        )}
                        {msg.message && (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                        )}
                        <div className={`flex items-center gap-1 mt-1.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <span className={`text-[10px] ${isMine ? 'text-white/60' : 'text-gray-400'}`}>
                            {fmtTime(msg.createdAt)}
                          </span>
                          {isMine && (
                            <span className={`text-[10px] ${msg.isRead ? 'text-white/80' : 'text-white/50'}`}>
                              {msg.isRead ? '✓✓' : '✓'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Pending file preview */}
            {pendingFile && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-t border-gray-100">
                {pendingFile.type === 'image' && pendingFile.preview ? (
                  <img src={pendingFile.preview} alt="preview" className="h-10 w-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                    {pendingFile.type === 'video' ? <Video className="w-5 h-5 text-primary-600" /> :
                     pendingFile.type === 'audio' ? <Music className="w-5 h-5 text-primary-600" /> :
                     <FileText className="w-5 h-5 text-primary-600" />}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{pendingFile.name}</p>
                  {pendingFile.size && <p className="text-xs text-gray-400">{fmtFileSize(pendingFile.size)}</p>}
                </div>
                <button onClick={clearPendingFile} className="text-gray-400 hover:text-red-500 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Attachment picker */}
            {showAttachPicker && (
              <div className="flex items-center gap-3 px-4 py-3 bg-white border-t border-gray-100">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 rounded-2xl bg-primary-50 border border-primary-100 px-5 py-3 hover:bg-primary-100 transition-colors"
                >
                  <Image className="w-5 h-5 text-primary-600" />
                  <span className="text-xs font-medium text-primary-700">Gallery</span>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 border border-gray-200 px-5 py-3 hover:bg-gray-100 transition-colors"
                >
                  <FileText className="w-5 h-5 text-gray-600" />
                  <span className="text-xs font-medium text-gray-700">Document</span>
                </button>
                <input ref={imageInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handleFileSelect(e)} />
                <input ref={fileInputRef} type="file" accept="*/*" className="hidden" onChange={(e) => handleFileSelect(e)} />
              </div>
            )}

            {/* Input bar */}
            <div className="flex items-end gap-2 px-3 py-3 bg-white border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setShowAttachPicker((p) => !p)}
                className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                  showAttachPicker ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Paperclip className="w-5 h-5" />
              </button>

              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowAttachPicker(false)}
                rows={1}
                placeholder={`Message ${getUserName(selectedUser)}…`}
                className="flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-primary-400 focus:bg-white transition-colors leading-relaxed"
                style={{ maxHeight: '120px', overflowY: 'auto' }}
                onInput={(e) => {
                  e.target.style.height = 'auto'
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
                }}
              />

              <button
                onClick={handleSend}
                disabled={sending || (!draft.trim() && !pendingFile)}
                className={`p-2.5 rounded-xl flex-shrink-0 transition-colors ${
                  !sending && (draft.trim() || pendingFile)
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-gray-400">
            <MessageSquare className="w-12 h-12 mb-3 text-gray-200" />
            <p className="text-base font-semibold text-gray-600">Your messages</p>
            <p className="text-sm mt-1 text-gray-400">Select a conversation or start a new one</p>
            <button onClick={openNewChat} className="btn-primary mt-4 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              New Conversation
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
